/**
 * Query definitions, one per screen's data need.
 *
 * Route loaders call `ensureQueryData` so the data is in cache before the
 * component renders — the same "fetch, then render" ordering the async server
 * components had — and components then read it with `useSuspenseQuery` and get
 * caching, deduplication and background refetch on top.
 */

import { queryOptions } from '@tanstack/react-query';

import { read, getMe, q } from './lib/client-data';
import type { Paged } from './lib/paged';
import type { StockValueLine, StockValueTotals } from './lib/stock-value';
import type { OrderStatus } from './lib/types';
import type {
  Assignee,
  IntegrationState,
  JobBlanks,
  ProductListing,
  StylePage,
  StockItemView,
  DocumentRow,
  DocumentVersion,
  Location,
  LowStockRow,
  PermissionMatrix,
  ItemCostChange,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderList,
  Recipe,
  RecipeLine,
  StaffMember,
  Supplier,
  SupplierPage,
  SupplierItemRow,
  ItemSupplierRow,
  Movement,
  StockResponse,
  StockTake,
  WasteRow,
} from './lib/stock-types';
import type { Job, Machine } from './lib/data/jobs';
import type {
  AuditLogRow,
  Brand,
  CatalogueItem,
  CommissionLedgerRow,
  Order,
  PartnershipConfig,
  PrintMethodRow,
  Product,
  PublishedProduct,
  PushCampaign,
  Quote,
  ReadyDesign,
  SiteConfig,
  Tenant,
} from './lib/client-data';

const FRESH = 30_000;

/**
 * Stock. This one does NOT go through the /api/data batch: the batch exists for
 * the fixtures-era read functions, and stock is read straight from the database
 * on the caller's session so that row level security and the column grants
 * apply (R-92). A separate route, a separate query.
 */
export const documentsQuery = queryOptions({
  queryKey: ['documents'],
  queryFn: async (): Promise<{ documents: DocumentRow[]; versions: DocumentVersion[] }> => {
    const res = await fetch('/api/documents', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Documents request failed (${res.status})`);
    }
    return (await res.json()) as { documents: DocumentRow[]; versions: DocumentVersion[] };
  },
  staleTime: 0,
});

export const adminUsersQuery = queryOptions({
  queryKey: ['admin', 'users'],
  queryFn: async (): Promise<{ users: StaffMember[] }> => {
    const res = await fetch('/api/users', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Users request failed (${res.status})`);
    }
    return (await res.json()) as { users: StaffMember[] };
  },
  staleTime: 0,
});

export const adminPermissionsQuery = queryOptions({
  queryKey: ['admin', 'permissions'],
  queryFn: async (): Promise<PermissionMatrix> => {
    const res = await fetch('/api/permissions/matrix', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Permissions request failed (${res.status})`);
    }
    return (await res.json()) as PermissionMatrix;
  },
  staleTime: 0,
});

export const adminSuppliersQuery = queryOptions({
  queryKey: ['admin', 'suppliers'],
  queryFn: async (): Promise<{ suppliers: Supplier[] }> => {
    const res = await fetch('/api/suppliers', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Suppliers request failed (${res.status})`);
    }
    return (await res.json()) as { suppliers: Supplier[] };
  },
  staleTime: 0,
});

async function readOrThrow<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Could not read ${what} (${res.status}).`);
  }
  return (await res.json()) as T;
}

/** One supplier, whole. Spec 013. Null when there is no such supplier. */
export const supplierQuery = (supplierId: string) => queryOptions({
  queryKey: ['admin', 'suppliers', supplierId],
  queryFn: async (): Promise<SupplierPage | null> => {
    const res = await fetch(`/api/suppliers/${supplierId}`, { credentials: 'same-origin' });
    if (res.status === 404) return null;
    return readOrThrow<SupplierPage>(res, 'the supplier');
  },
  staleTime: 0,
});

/** The items a supplier sells, one page. Spec 013 FR-1323. */
export const supplierItemsQuery = (supplierId: string, page = 1) => queryOptions({
  queryKey: ['admin', 'suppliers', supplierId, 'items', page],
  queryFn: async (): Promise<Paged<SupplierItemRow> & { costs_visible: boolean }> => {
    const res = await fetch(`/api/suppliers/${supplierId}/items?page=${page}`, { credentials: 'same-origin' });
    return readOrThrow(res, 'the items of the supplier');
  },
  staleTime: 0,
});

/** The suppliers of one stock item. Spec 013 FR-1324. */
export const itemSuppliersQuery = (itemId: string) => queryOptions({
  queryKey: ['stock-item', itemId, 'suppliers'],
  queryFn: async (): Promise<{ rows: ItemSupplierRow[]; costs_visible: boolean; may_edit: boolean }> => {
    const res = await fetch(`/api/items/${itemId}/suppliers`, { credentials: 'same-origin' });
    return readOrThrow(res, 'the suppliers of the item');
  },
  staleTime: 0,
});

/**
 * The categories the catalogue already uses. Tracker #12.
 *
 * There is no list of categories in the code. Guy adds one by typing it on a
 * style, and it is on this list for everybody from then on.
 */
export const adminProductCategoriesQuery = queryOptions({
  queryKey: ['admin', 'product-categories'],
  queryFn: async (): Promise<{ categories: string[] }> => {
    const res = await fetch('/api/product-categories', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Categories request failed (${res.status})`);
    }
    return (await res.json()) as { categories: string[] };
  },
  staleTime: 0,
});

/** One of the three lists (FR-1243), a page at a time. */
export const adminPurchaseOrdersQuery = (list: PurchaseOrderList = 'open', page = 1) => queryOptions({
  queryKey: ['admin', 'purchase-orders', list, page],
  queryFn: async (): Promise<Paged<PurchaseOrder> & { costs_visible: boolean; today: string }> => {
    const res = await fetch(`/api/purchase-orders?list=${list}&page=${page}&limit=50`, {
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Purchase orders failed (${res.status})`);
    }
    return (await res.json()) as Paged<PurchaseOrder> & { costs_visible: boolean; today: string };
  },
  staleTime: 0,
});

/** One order whole. Spec 012. */
export const purchaseOrderQuery = (id: string) => queryOptions({
  queryKey: ['admin', 'purchase-orders', 'one', id],
  queryFn: async (): Promise<PurchaseOrderDetail> => {
    const res = await fetch(`/api/purchase-orders/${id}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Purchase order failed (${res.status})`);
    }
    return (await res.json()) as PurchaseOrderDetail;
  },
  staleTime: 0,
});

/** The cost history of one item. Spec 012, FR-1273. costs.view. */
export const itemCostHistoryQuery = (itemId: string) => queryOptions({
  queryKey: ['stock-item', itemId, 'cost-history'],
  queryFn: async (): Promise<{ rows: ItemCostChange[] }> => {
    const res = await fetch(`/api/items/${itemId}/cost-history`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Cost history failed (${res.status})`);
    return (await res.json()) as { rows: ItemCostChange[] };
  },
  staleTime: 0,
});

export const adminRecipesQuery = queryOptions({
  queryKey: ['admin', 'recipes'],
  queryFn: async (): Promise<{
    recipes: Recipe[];
    lines: RecipeLine[];
    products: { id: string; name: string }[];
    methods: { key: string; name: string }[];
    materials: { id: string; code: string; name: string; base_uom: string }[];
  }> => {
    const res = await fetch('/api/recipes', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Recipes request failed (${res.status})`);
    }
    return (await res.json()) as {
      recipes: Recipe[];
      lines: RecipeLine[];
      products: { id: string; name: string }[];
      methods: { key: string; name: string }[];
      materials: { id: string; code: string; name: string; base_uom: string }[];
    };
  },
  staleTime: 0,
});

/**
 * One page of product records, with what the shop needs to sell them. R-212.
 *
 * The handler has paged since it was written and this asked for `/api/products`
 * with no arguments, so the screen has always shown the first 50 of 380 and
 * said nothing. A server that pages and a client that ignores the page is the
 * same bug as no paging at all, with the evidence one layer further away.
 */
/** One style, whole: spec 010. */
/**
 * The colours and sizes the catalogue already uses, as suggestions on the
 * style page. Spec 011, FR-1114. Read once a minute at most: a suggestion that
 * is a minute old is still a good suggestion.
 */
export const styleOptionsQuery = queryOptions({
  queryKey: ['admin', 'style-options'],
  queryFn: async (): Promise<{
    colours: { name: string; hex: string; uses: number }[];
    sizes: { name: string; uses: number }[];
  }> => {
    const res = await fetch('/api/styles/options', { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Could not read the suggestions (${res.status}).`);
    return res.json();
  },
  staleTime: 60_000,
});

export const styleQuery = (productId: string) => queryOptions({
  queryKey: ['admin', 'style', productId],
  queryFn: async (): Promise<StylePage | null> => {
    const res = await fetch(`/api/styles/${productId}`, { credentials: 'same-origin' });
    // Null, so the page can say "no such style" rather than fail.
    if (res.status === 404) return null;
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Could not read the style (${res.status}).`);
    }
    return (await res.json()) as StylePage;
  },
  staleTime: 0,
});

export const adminProductListingQuery = (
  params: { page?: number; q?: string } = {},
) => queryOptions({
  queryKey: ['admin', 'product-listing', params],
  queryFn: async (): Promise<Paged<ProductListing> & { incomplete: number }> => {
    const search = new URLSearchParams({ page: String(params.page ?? 1), limit: '50' });
    if (params.q?.trim()) search.set('q', params.q.trim());
    const res = await fetch(`/api/products?${search.toString()}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the products.');
    return res.json() as Promise<Paged<ProductListing> & { incomplete: number }>;
  },
  staleTime: 0,
});

export const adminLowStockQuery = queryOptions({
  queryKey: ['admin', 'stock', 'low'],
  queryFn: async (): Promise<{
    rows: LowStockRow[];
    suppliers: Array<{ id: string; name: string }>;
    /**
     * Counted on the server so three screens cannot each work it out
     * differently. `covered` is the number a buyer can stop worrying about
     * today, because a delivery is already on its way. R-205.
     */
    summary: { short: number; covered: number; to_buy: number };
  }> => {
    const res = await fetch('/api/stock/low', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Reorder request failed (${res.status})`);
    }
    return (await res.json()) as {
      rows: LowStockRow[];
      suppliers: Array<{ id: string; name: string }>;
      summary: { short: number; covered: number; to_buy: number };
    };
  },
  staleTime: 0,
});

export interface WasteGroup {
  bucket: string;
  entries: number;
  qty: number;
  cost_cents: number | null;
}

/**
 * The waste report, grouped by the SERVER.
 *
 * Not a page, because the screen never shows an individual spoil: it shows the
 * cluster, and a cluster counted from fifty rows reads exactly like a cluster
 * counted from all of them. The grouping goes where the rows are. R-231.
 */
export const adminWasteQuery = (by: 'reason' | 'item' | 'operator' | 'machine') =>
  queryOptions({
    queryKey: ['admin', 'waste', by],
    queryFn: async (): Promise<{
      by: string;
      rows: WasteGroup[];
      totals: { entries: number; qty: number; cost_cents: number | null };
    }> => {
      const res = await fetch(`/api/reports/waste?by=${by}`, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Waste request failed (${res.status})`);
      }
      return (await res.json()) as {
        by: string;
        rows: WasteGroup[];
        totals: { entries: number; qty: number; cost_cents: number | null };
      };
    },
    staleTime: 0,
  });

export const adminLocationsQuery = queryOptions({
  queryKey: ['admin', 'locations'],
  queryFn: async (): Promise<{ locations: Location[] }> => {
    const res = await fetch('/api/locations', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Locations request failed (${res.status})`);
    }
    return (await res.json()) as { locations: Location[] };
  },
  staleTime: 0,
});

export const itemMovementsQuery = (itemId: string, page = 1) =>
  queryOptions({
    queryKey: ['admin', 'stock', itemId, 'movements', page],
    queryFn: async (): Promise<Paged<Movement>> => {
      const res = await fetch(`/api/stock/${itemId}/movements?page=${page}&limit=50`, {
        credentials: 'same-origin',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Movements request failed (${res.status})`);
      }
      return (await res.json()) as Paged<Movement>;
    },
    staleTime: 0,
  });

export const adminStockTakesQuery = (page = 1) => queryOptions({
  queryKey: ['admin', 'stock-takes', page],
  queryFn: async (): Promise<Paged<StockTake>> => {
    const res = await fetch(`/api/stock-takes?page=${page}&limit=25`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Stocktake request failed (${res.status})`);
    }
    return (await res.json()) as Paged<StockTake>;
  },
  staleTime: 0,
});

/**
 * One page of stock, searched and filtered by the SERVER.
 *
 * It used to be `?limit=500` with no arguments, and the screen searched the 500
 * it was given. `stock_free` holds 1,057 rows, so the screen was searching two
 * thirds of the shelf and reporting the answer as the whole (R-231). Search,
 * the kind filter and the low filter all decide which rows belong in the page,
 * so all three go with the request.
 *
 * The filters are part of the query key. They have to be: a cached page one for
 * "black tee" is not page one for "hoodie", and React Query cannot know that
 * unless the key says so.
 */
export interface StockParams {
  page?: number;
  limit?: number;
  q?: string;
  kind?: 'all' | 'material' | 'garment';
  /** Spec 014, FR-1406. One class, or every class but Archived when absent. */
  cls?: string;
  /** Spec 014, FR-1403. Archived items are hidden unless this is on. */
  archived?: boolean;
  low?: boolean;
  /**
   * The other list: items with no stock line anywhere. Tracker #13.
   *
   * Not a narrowing of `low`, and the server refuses the two together. A stock
   * line only exists once stock has arrived somewhere, so items that have
   * never had any were absent from this screen and nothing said so.
   */
  none?: boolean;
  /**
   * Every item, on a shelf or not, in one list. Tracker #13.
   *
   * Guy, 20 October: "So I still cant get a complete list". The three lists
   * above are one choice of three, and none of them is all of the stock.
   */
  all?: boolean;
  item?: string;
  location?: string;
}

export const adminStockQuery = (params: StockParams = {}) => queryOptions({
  queryKey: ['admin', 'stock', params],
  queryFn: async (): Promise<StockResponse> => {
    const search = new URLSearchParams();
    search.set('page', String(params.page ?? 1));
    search.set('limit', String(params.limit ?? 50));
    if (params.q?.trim()) search.set('q', params.q.trim());
    if (params.kind && params.kind !== 'all') search.set('kind', params.kind);
    if (params.cls) search.set('class', params.cls);
    if (params.archived) search.set('archived', '1');
    if (params.low) search.set('low', '1');
    if (params.none) search.set('none', '1');
    if (params.all) search.set('all', '1');
    if (params.item) search.set('item', params.item);
    if (params.location) search.set('location', params.location);

    const res = await fetch(`/api/stock?${search.toString()}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      // Surface the reason. A stock screen that silently shows nothing is
      // indistinguishable from a warehouse that is empty.
      throw new Error(body?.error?.message ?? `Stock request failed (${res.status})`);
    }
    return (await res.json()) as StockResponse;
  },
  staleTime: 0,
});

/**
 * Variants that match what somebody typed. At most 25, decided by the server.
 *
 * `enabled` on a term shorter than two characters, so an empty box does not ask
 * the database for the catalogue.
 */
export const variantSearchQuery = (term: string) => queryOptions({
  queryKey: ['variants', 'search', term],
  queryFn: async (): Promise<{ rows: Array<{ id: string; label: string }> }> => {
    const res = await fetch(`/api/variants/search?q=${encodeURIComponent(term)}`, {
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error('Could not search the variants.');
    return res.json() as Promise<{ rows: Array<{ id: string; label: string }> }>;
  },
  enabled: term.trim().length >= 2,
  staleTime: FRESH,
});

/**
 * One page of the order register.
 *
 * `status` is a filter word rather than a status: "all" and "open" are both
 * things a person asks for, and "open" means the same five statuses here as it
 * does on the pipeline because the server owns the definition.
 */
/**
 * The customer register. 6,590 rows, so never read without a page.
 */
export const adminCustomersQuery = (
  params: { page?: number; q?: string; kind?: string; owing?: boolean } = {},
) => queryOptions({
  queryKey: ['admin', 'customers', params],
  queryFn: async (): Promise<Paged<unknown> & { b2b: number; owing: number }> => {
    const search = new URLSearchParams({ page: String(params.page ?? 1), limit: '50' });
    if (params.q?.trim()) search.set('q', params.q.trim());
    if (params.kind && params.kind !== 'all') search.set('kind', params.kind);
    if (params.owing) search.set('owing', '1');

    const res = await fetch(`/api/customers?${search.toString()}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Customer request failed (${res.status})`);
    }
    return (await res.json()) as Paged<unknown> & { b2b: number; owing: number };
  },
  staleTime: 0,
});

/** One customer, with the last twenty things they bought. */
export const adminCustomerQuery = (customerId: string) => queryOptions({
  queryKey: ['admin', 'customer', customerId],
  queryFn: async () => {
    const res = await fetch(`/api/customers/${customerId}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? 'Could not read the customer.');
    }
    return (await res.json()) as {
      customer: Record<string, unknown>;
      /** Spec 016. Every contact, removed ones too. */
      contacts: Array<Record<string, unknown>>;
      orders: Array<Record<string, unknown>>;
      order_count: number;
    };
  },
  staleTime: 0,
});

export const adminOrderListQuery = (
  params: { page?: number; q?: string; status?: string } = {},
) => queryOptions({
  queryKey: ['admin', 'order-list', params],
  queryFn: async (): Promise<Paged<unknown> & {
    counts: Record<string, number>;
    open: number;
  }> => {
    const search = new URLSearchParams({ page: String(params.page ?? 1), limit: '50' });
    if (params.q?.trim()) search.set('q', params.q.trim());
    if (params.status && params.status !== 'all') search.set('status', params.status);

    const res = await fetch(`/api/orders?${search.toString()}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Order request failed (${res.status})`);
    }
    return (await res.json()) as Paged<unknown> & {
      counts: Record<string, number>; open: number;
    };
  },
  staleTime: 0,
});

/* -------------------------------------------------------------------------- */
/* Xero. Spec 004.                                                            */
/* -------------------------------------------------------------------------- */

export interface XeroStatus {
  configured: boolean;
  connection: {
    tenant_id: string; org_name: string | null; state: string;
    last_success_at: string | null; day_remaining: number | null;
    minute_remaining: number | null; attention_reason: string | null;
    retry_after: string | null;
  } | null;
  config: {
    sales_account_code: string; purchase_account_code: string;
    cogs_account_code: string | null; inventory_account_code: string | null;
    sales_tax_type: string; purchase_tax_type: string;
    invoice_due_days: number; read_back_minutes: number;
    codes_are_temporary: boolean;
  } | null;
  needs_person: number;
  blocked_reason: string | null;
  /** Spec 017. Jobs waiting in the queue to be sent. */
  waiting?: number;
  /** Spec 017 FR-1721. Sent one at a time and checked, of the five needed. */
  one_at_a_time?: { checked: number; needed: number; done: boolean };
  /** Spec 017 FR-1720. The latest audit of the connected organisation. */
  audit?: { passed: boolean; ran_at: string | null; reason: string | null };
  /** Spec 017 FR-1723. XERO_SCHEDULE=on on the server. */
  schedule_on?: boolean;
}

export interface SettingsResponse {
  company: { id: string; slug: string; legal_name: string; trading_name: string | null; abn: string | null } | null;
  values: Record<string, unknown>;
  fields: Array<{ key: string; label: string; kind: string }>;
}

export interface HelpArticle {
  slug: string; title: string; section: string; summary: string; sort: number;
}

export const adminMaterialsQuery = queryOptions({
  queryKey: ['admin', 'materials'],
  queryFn: async (): Promise<{
    materials: Array<Record<string, unknown>>;
    uoms: Array<{ code: string; name: string }>;
  }> => {
    const res = await fetch('/api/materials', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the materials.');
    return await res.json();
  },
  staleTime: 0,
});

export const adminHelpQuery = queryOptions({
  queryKey: ['admin', 'help'],
  queryFn: async (): Promise<{ articles: HelpArticle[] }> => {
    const res = await fetch('/api/help', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the manual.');
    return (await res.json()) as { articles: HelpArticle[] };
  },
  staleTime: FRESH,
});

export const helpArticleQuery = (slug: string | null) => queryOptions({
  queryKey: ['admin', 'help', 'article', slug],
  queryFn: async (): Promise<{ article: HelpArticle & { body: string } }> => {
    const res = await fetch(`/api/help/${slug}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read that page.');
    return (await res.json()) as { article: HelpArticle & { body: string } };
  },
  enabled: Boolean(slug),
  staleTime: FRESH,
});

/** Postgres does the ranking. Two characters minimum, so an empty box asks nothing. */
export const helpSearchQuery = (term: string) => queryOptions({
  queryKey: ['admin', 'help', 'search', term],
  queryFn: async (): Promise<{
    query: string;
    results: Array<HelpArticle & { snippet: string; rank: number; loose: boolean }>;
  }> => {
    const res = await fetch(`/api/help/search?q=${encodeURIComponent(term)}`, {
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error('Could not search the manual.');
    return await res.json();
  },
  enabled: term.trim().length >= 2,
  staleTime: FRESH,
});

export const adminSettingsQuery = queryOptions({
  queryKey: ['admin', 'settings'],
  queryFn: async (): Promise<SettingsResponse> => {
    const res = await fetch('/api/settings', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the settings.');
    return (await res.json()) as SettingsResponse;
  },
  staleTime: 0,
});

export const xeroStatusQuery = queryOptions({
  queryKey: ['admin', 'xero', 'status'],
  queryFn: async (): Promise<XeroStatus> => {
    const res = await fetch('/api/xero/status', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the Xero status.');
    return (await res.json()) as XeroStatus;
  },
  staleTime: 0,
});

export interface XeroChart {
  accounts: Array<{ code: string; name: string; type: string | null }>;
  tax_rates: Array<{ tax_type: string; name: string; rate: number | null }>;
  /** False until somebody has run the settings pull. Not an error. */
  pulled: boolean;
}

export const xeroChartQuery = queryOptions({
  queryKey: ['admin', 'xero', 'chart'],
  queryFn: async (): Promise<XeroChart> => {
    const res = await fetch('/api/xero/chart', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the chart of accounts.');
    return (await res.json()) as XeroChart;
  },
  staleTime: FRESH,
});

export const xeroLogQuery = (page = 1) => queryOptions({
  queryKey: ['admin', 'xero', 'log', page],
  queryFn: async (): Promise<Paged<unknown>> => {
    const res = await fetch(`/api/xero/log?page=${page}&limit=50`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the Xero log.');
    return (await res.json()) as Paged<unknown>;
  },
  staleTime: 0,
});

export const xeroQueueQuery = (page = 1) => queryOptions({
  queryKey: ['admin', 'xero', 'queue', page],
  queryFn: async (): Promise<Paged<unknown>> => {
    const res = await fetch(`/api/xero/queue?page=${page}&limit=50`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the Xero queue.');
    return (await res.json()) as Paged<unknown>;
  },
  staleTime: 0,
});

/** Reports, Stock value. Spec 017, FR-1701 to FR-1713. */
export interface StockValueResponse {
  as_at: string;
  today: boolean;
  history_starts: string | null;
  closed_at: string | null;
  show_cost: boolean;
  totals: StockValueTotals;
  rows: StockValueLine[];
}

export const stockValueQuery = (asAt: string | null) => queryOptions({
  queryKey: ['admin', 'reports', 'stock-value', asAt ?? 'today'],
  queryFn: () => getJson<StockValueResponse>(
    `/api/reports/stock-value${asAt ? `?as_at=${encodeURIComponent(asAt)}` : ''}`, 'the stock value'),
  staleTime: 0,
});

/** Every shelf one item sits on. Bounded by the number of locations. */
export const stockItemQuery = (itemId: string) =>
  adminStockQuery({ item: itemId, limit: 200 });

/**
 * One stock item, whether or not it has stock anywhere. Spec 010: the panel
 * read the item from the stock rows, and an item with no stock has none.
 */
export const itemQuery = (itemId: string) => queryOptions({
  queryKey: ['stock-item', itemId, 'record'],
  queryFn: async (): Promise<StockItemView> => {
    const res = await fetch(`/api/items/${itemId}`, { credentials: 'same-origin' });
    if (res.status === 404) return { item: null, listing: null, supplier: null, cost: null };
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Could not read the item (${res.status}).`);
    }
    return (await res.json()) as StockItemView;
  },
  staleTime: 0,
});

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: getMe,
  staleTime: FRESH,
});

/** The store shell's own data. Sits on every storefront page, so cache it long. */
/**
 * Who this shop belongs to. R-140.
 *
 * The root route loads it, so the header, the footer and every admin screen
 * read one answer instead of three hard-coded names that disagreed.
 */
export const tenantQuery = queryOptions({
  queryKey: ['tenant'],
  queryFn: async (): Promise<Tenant> => {
    const [tenant] = await read<[Tenant]>(q.tenant());
    return tenant;
  },
  // The shop's own name does not change while somebody is shopping.
  staleTime: 5 * 60_000,
});

export const footerQuery = queryOptions({
  queryKey: ['footer'],
  queryFn: async () => {
    const [brands, config] = await read<[Brand[], SiteConfig]>(q.brands(), q.siteConfig());
    return { brands, config };
  },
  staleTime: FRESH,
});

export const homeQuery = queryOptions({
  queryKey: ['home'],
  queryFn: async () => {
    const [brands, catalogue, methods] = await read<[Brand[], CatalogueItem[], PrintMethodRow[]]>(
      q.brands(),
      q.catalogue(),
      q.printMethods(),
    );
    return { brands, catalogue, methods };
  },
  staleTime: FRESH,
});

export const shopQuery = (brand?: string) =>
  queryOptions({
    queryKey: ['shop', brand ?? 'all'],
    queryFn: async () => {
      const calls = brand
        ? [q.brands(), q.catalogue(brand), q.brand(brand)]
        : [q.brands(), q.catalogue()];
      const [brands, catalogue, activeBrand] = await read<
        [Brand[], CatalogueItem[], Brand | null]
      >(...calls);
      return { brands, catalogue, activeBrand: activeBrand ?? null };
    },
    staleTime: FRESH,
  });

export const productQuery = (id: string) =>
  queryOptions({
    queryKey: ['product', id],
    queryFn: async () => {
      // Two round trips, not one: the blank and the print method are addressed
      // by ids that only exist once the item is known. The server component
      // awaited in the same order for the same reason.
      const [item, catalogue, config] = await read<
        [CatalogueItem | null, CatalogueItem[], SiteConfig]
      >(q.catalogueItem(id), q.catalogue(), q.siteConfig());

      if (!item) return { item: null, blank: null, method: null, catalogue, config };

      const [blank, method] = await read<[Product | null, PrintMethodRow | null]>(
        q.product(item.product_id),
        q.printMethod(item.method_key),
      );
      return { item, blank, method, catalogue, config };
    },
    staleTime: FRESH,
  });

export const customQuery = queryOptions({
  queryKey: ['custom'],
  queryFn: async () => {
    const [products, methods] = await read<[Product[], PrintMethodRow[]]>(
      q.products(),
      q.printMethods(),
    );
    return { products, methods };
  },
  staleTime: FRESH,
});

export const studioQuery = (productId: string) =>
  queryOptions({
    queryKey: ['studio', productId],
    queryFn: async () => {
      const [product, products, methods, designs] = await read<
        [Product | null, Product[], PrintMethodRow[], ReadyDesign[]]
      >(q.product(productId), q.products(), q.printMethods(), q.readyDesigns());
      return { product, products, methods, designs };
    },
    staleTime: FRESH,
  });

/**
 * Signed-out is a normal outcome here, not an error: the page renders a "sign
 * in to see your orders" state. So resolve the session first and only ask for
 * rows when there is someone to ask about — otherwise the own-row reads would
 * 401 and the query would throw where the UI wanted an empty state.
 */
export const ordersQuery = queryOptions({
  queryKey: ['orders'],
  queryFn: async () => {
    const me = await getMe();
    if (!me.user) return { me, orders: [] as Order[], quotes: [] as Quote[] };
    const [orders, quotes] = await read<[Order[], Quote[]]>(q.orders(), q.quotesForUser());
    return { me, orders, quotes };
  },
  staleTime: 0,
});

export const orderQuery = (ref: string, token?: string) =>
  queryOptions({
    queryKey: ['order', ref, token ?? ''],
    queryFn: async () => {
      const [order, config] = await read<[Order | null, SiteConfig]>(
        // The token is what makes the link work for somebody who is not signed
        // in. Without it this page was a 404 for every guest (R-154).
        q.orderByRef(ref, token),
        q.siteConfig(),
      );
      return { order, config };
    },
    staleTime: 0,
  });

export const quoteQuery = (ref: string, token?: string) =>
  queryOptions({
    queryKey: ['quote', ref, token ?? ''],
    queryFn: async () => {
      const [quote] = await read<[Quote | null]>(q.quoteByRef(ref, token));
      const [product] = quote?.product_id
        ? await read<[Product | null]>(q.product(quote.product_id))
        : [null];
      return { quote, product };
    },
    staleTime: 0,
  });

export const accountQuery = queryOptions({
  queryKey: ['account'],
  queryFn: async () => {
    const me = await getMe();
    if (!me.user) return { me, orders: [] as Order[], quotes: [] as Quote[] };
    const [orders, quotes] = await read<[Order[], Quote[]]>(q.orders(), q.quotesForUser());
    return { me, orders, quotes };
  },
  staleTime: 0,
});

/* ------------------------------- admin ----------------------------------- */

export const adminShellQuery = queryOptions({
  queryKey: ['admin', 'shell'],
  queryFn: async () => {
    const me = await getMe();

    // What this caller may do, asked of the database rather than inferred from
    // a role name. The navigation used to branch on `isAdmin`, which meant a
    // permission granted through the new permissions screen changed what the
    // database allowed and not what the person could see (R-120).
    let can: Record<string, string[]> = {};
    const res = await fetch('/api/permissions/me', { credentials: 'same-origin' });
    if (res.ok) {
      ({ can } = (await res.json()) as { can: Record<string, string[]> });
    }

    if (!me.isAdmin) return { me, can, quotes: [] as Quote[] };
    const [quotes] = await read<[Quote[]]>(q.quotes());
    return { me, can, quotes };
  },
  staleTime: 0,
});

export interface DashboardSummary {
  range: string;
  orders: number;
  revenue_cents: number;
  /** The GST taken out of revenue. Tracker #9. */
  gst_cents?: number;
  /** Null when this caller may not see money. */
  cost_cents: number | null;
  costed_revenue_cents: number | null;
  /** 0 to 1. How much of the revenue has a real cost behind it. */
  cost_coverage: number;
  show_cost: boolean;
  statuses: Array<{ status: OrderStatus; n: number }>;
  /**
   * Where the orders came from, over the same range. Spec 006 FR-610.
   *
   * `code: null` is the 12,656 imported from Shopify, which nobody asked. It
   * is a row rather than an omission, because "we never asked" and "the
   * customer said something else" are different facts, and a report that hides
   * the first one overstates every other line in it.
   */
  sources: Array<{
    code: string | null; label: string; orders: number; revenue_cents: number;
  }>;
  recent: Array<{
    id: string; ref: string; created_at: string;
    channel: string; status: OrderStatus; total_cents: number;
  }>;
}

/**
 * The dashboard's numbers, summed by the SERVER over the whole range.
 *
 * It used to read every order and add them up here, which is a fine design for
 * a studio with fifty orders and a wrong answer for one with 12,656: the read
 * was capped at 200 and the total was the total of those 200. R-235.
 *
 * The quotes queue stays a separate read because it is a short list of things
 * to do rather than a number.
 */
export const adminDashboardQuery = (range = '30d') => queryOptions({
  queryKey: ['admin', 'dashboard', range],
  queryFn: async (): Promise<{ summary: DashboardSummary; quotes: Quote[] }> => {
    const [res, quotes] = await Promise.all([
      fetch(`/api/dashboard?range=${encodeURIComponent(range)}`, { credentials: 'same-origin' }),
      read<[Quote[]]>(q.quotes()).then(([qs]) => qs),
    ]);
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Dashboard request failed (${res.status})`);
    }
    return { summary: (await res.json()) as DashboardSummary, quotes };
  },
  staleTime: 0,
});

/**
 * Who a job can be given to. Internal roles only, from `user_roles`, so it
 * cannot drift from who actually works here. R-200.
 */
export const staffQuery = queryOptions({
  queryKey: ['admin', 'staff'],
  queryFn: async (): Promise<{ staff: Assignee[] }> => {
    const res = await fetch('/api/staff', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not read the staff list.');
    return res.json() as Promise<{ staff: Assignee[] }>;
  },
  staleTime: 5 * 60_000,
});

/**
 * Gate 2, per job: what it needs, what is free, what is on order and when.
 *
 * Asked per card rather than for the whole board, because most cards never need
 * it: a job that is ready, complete or print-on-demand has nothing to check.
 * R-202.
 */
export const jobBlanksQuery = (ref: string) =>
  queryOptions({
    queryKey: ['admin', 'jobs', ref, 'blanks'],
    queryFn: async (): Promise<JobBlanks> => {
      const res = await fetch(`/api/jobs/${ref}/blanks`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Could not check the blanks.');
      return res.json() as Promise<JobBlanks>;
    },
    staleTime: 60_000,
  });

export const adminJobsQuery = queryOptions({
  queryKey: ['admin', 'jobs'],
  queryFn: async () => {
    const [jobs, machines] = await read<[Job[], Machine[]]>(q.jobs(), q.machines());
    return { jobs, machines };
  },
  staleTime: 0,
});

/**
 * The machines screen, which sees retired machines and the board does not.
 *
 * Separate from `adminJobsQuery` on purpose. The board asks "what can I put
 * work on"; this screen asks "what do we own". One read cannot answer both,
 * and merging them is how a retired press got back onto the board.
 */
export const adminMachinesQuery = queryOptions({
  queryKey: ['admin', 'machines'],
  queryFn: async () => {
    const [jobs, machines] = await read<[Job[], Machine[]]>(q.jobs(), q.machines(true));
    return { jobs, machines };
  },
  staleTime: 0,
});

export const adminOrdersQuery = queryOptions({
  queryKey: ['admin', 'orders'],
  queryFn: async () => {
    const [orders] = await read<[Order[]]>(q.allOrders());
    return { orders };
  },
  staleTime: 0,
});

/** The quote register, paged, with the tab counts from the database. */
export const adminQuoteListQuery = (
  params: { page?: number; filter?: string; q?: string } = {},
) => queryOptions({
  queryKey: ['admin', 'quote-list', params],
  queryFn: async (): Promise<Paged<Quote> & { counts: Record<string, number> }> => {
    const search = new URLSearchParams({ page: String(params.page ?? 1), limit: '50' });
    search.set('filter', params.filter ?? 'open');
    if (params.q?.trim()) search.set('q', params.q.trim());

    const res = await fetch(`/api/quote-list?${search.toString()}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Quote request failed (${res.status})`);
    }
    return (await res.json()) as Paged<Quote> & { counts: Record<string, number> };
  },
  staleTime: 0,
});

export const adminQuotesQuery = queryOptions({
  queryKey: ['admin', 'quotes'],
  queryFn: async () => {
    const [quotes] = await read<[Quote[]]>(q.quotes());
    return { quotes };
  },
  staleTime: 0,
});

export const adminQuoteQuery = (ref: string) =>
  queryOptions({
    queryKey: ['admin', 'quote', ref],
    queryFn: async () => {
      const [quote] = await read<[Quote | null]>(q.quoteByRef(ref));
      const [product] = quote?.product_id
        ? await read<[Product | null]>(q.product(quote.product_id))
        : [null];
      return { quote, product };
    },
    staleTime: 0,
  });

/**
 * What an order can be taken for, and how the customer reached us. Spec 006.
 *
 * The LIVE published rows only: a person on the telephone cannot sell
 * something that is switched off, and offering it is how an order gets taken
 * for a product that was withdrawn.
 *
 * 676 rows today, so this asks for the live ones in one page of 500 rather
 * than paging a dropdown. If it ever outgrows that the control becomes a
 * search, and the ceiling is named here so the day it matters is visible.
 */
export const newOrderQuery = queryOptions({
  queryKey: ['admin', 'new-order'],
  queryFn: async (): Promise<{ published: Array<{ id: string; title: string; retail_price_cents: number | null }> }> => {
    const res = await fetch('/api/published?page=1&limit=500&state=live', {
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Published request failed (${res.status})`);
    }
    const paged = (await res.json()) as { rows?: Array<Record<string, unknown>> };
    return {
      published: (paged.rows ?? []).map((r) => ({
        id: String(r.id),
        title: String(r.title ?? ''),
        retail_price_cents: r.retail_price_cents == null ? null : Number(r.retail_price_cents),
      })),
    };
  },
  staleTime: FRESH,
});

/**
 * The sources, read from the table rather than hard coded.
 *
 * The business will add a channel the day it starts using one, and a dropdown
 * that needs a deploy is a dropdown that stays wrong. Spec 006 FR-602.
 */
export const orderSourcesQuery = queryOptions({
  queryKey: ['admin', 'order-sources'],
  queryFn: async (): Promise<{ sources: Array<{ code: string; label: string }> }> => {
    const res = await fetch('/api/order-sources', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Sources request failed (${res.status})`);
    }
    return (await res.json()) as { sources: Array<{ code: string; label: string }> };
  },
  staleTime: FRESH,
});

/** The published rows the Publishing screen lists. 676 today, and it grows. */
export const adminPublishedQuery = (
  params: { page?: number; q?: string; state?: string } = {},
) => queryOptions({
  queryKey: ['admin', 'published', params],
  queryFn: async (): Promise<Paged<Record<string, unknown>> & { live: number }> => {
    const search = new URLSearchParams({ page: String(params.page ?? 1), limit: '50' });
    if (params.q?.trim()) search.set('q', params.q.trim());
    if (params.state && params.state !== 'all') search.set('state', params.state);

    const res = await fetch(`/api/published?${search.toString()}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Published request failed (${res.status})`);
    }
    return (await res.json()) as Paged<Record<string, unknown>> & { live: number };
  },
  staleTime: 0,
});

export const adminProductsQuery = queryOptions({
  queryKey: ['admin', 'products'],
  queryFn: async () => {
    const [brands, catalogue, methods, products, designs] = await read<
      [Brand[], CatalogueItem[], PrintMethodRow[], Product[], ReadyDesign[]]
    >(q.brands(), q.catalogue(), q.printMethods(), q.products(), q.readyDesigns());
    return { brands, catalogue, methods, products, designs };
  },
  staleTime: FRESH,
});

export const adminDesignsQuery = queryOptions({
  queryKey: ['admin', 'designs'],
  queryFn: async () => {
    const [brands, designs] = await read<[Brand[], ReadyDesign[]]>(
      q.brands(),
      q.readyDesigns(),
    );
    return { brands, designs };
  },
  staleTime: FRESH,
});

/**
 * What a staff quote can be built from. R-275.
 *
 * EVERY method, not only the active ones, and that is not a copy of the
 * mistake R-238 fixed. Every one of the client's five decoration methods is
 * switched off today, because every rate on them is zero and
 * `no_quotable_method_without_rates` refuses to let a method be quotable at no
 * price. The active-only list is therefore empty, and a form offering an empty
 * dropdown teaches the person that the screen is broken.
 *
 * So the form shows them all and says which have no rates. The estimate on an
 * unpriced method covers the garment alone, which is the truth, and the
 * confirmed price is set on the quote by hand as it always is.
 *
 * `allPrintMethods` is gated on `pricing.view`, which admin and manager hold.
 * They are exactly the two roles with `quotes.create`.
 */
export const newQuoteQuery = queryOptions({
  queryKey: ['admin', 'new-quote'],
  queryFn: async () => {
    const [products, methods] = await read<[Product[], PrintMethodRow[]]>(
      q.products(),
      q.allPrintMethods(),
    );
    return { products, methods };
  },
  staleTime: FRESH,
});

export const adminMethodsQuery = queryOptions({
  queryKey: ['admin', 'methods'],
  queryFn: async () => {
    // EVERY method, not only the active ones. This screen exists to switch a
    // method on, and it read the active-only list, so the only methods it
    // could show were the ones that did not need it. R-238.
    const [methods, audit] = await read<[PrintMethodRow[], AuditLogRow[]]>(
      q.allPrintMethods(),
      q.auditLog(),
    );
    return { methods, audit };
  },
  staleTime: 0,
});



/** The Shopify bridge: is it connected, what is waiting, what needs a person. */
export const adminIntegrationsQuery = queryOptions({
  queryKey: ['admin', 'integrations'],
  queryFn: async (): Promise<IntegrationState> => {
    const res = await fetch('/api/integrations', { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? `Integrations request failed (${res.status})`);
    }
    return (await res.json()) as IntegrationState;
  },
  staleTime: 0,
});

/* -------------------------------------------------------------------------- */
/* Prospects. Spec 009.                                                       */
/* -------------------------------------------------------------------------- */

export interface ProspectChoice {
  id: string; list: string; key: string | null; label: string; sort: number; active: boolean;
}

export interface Prospect {
  id: string; name: string; contact_name: string | null; contact_role: string | null;
  email: string | null; phone: string | null; street: string | null; suburb: string | null;
  postcode: string | null; website: string | null; category_id: string | null;
  subcategory: string | null; region_id: string | null; status_id: string;
  source_id: string | null; ownership_id: string | null; operator: string | null;
  parent_group: string | null; group_locations: string | null;
  garment_interest: string | null; decoration_notes: string | null; notes: string | null;
  follow_up_on: string | null; follow_up_note: string | null; lost_reason: string | null;
  customer_id: string | null; active: boolean; created_by: string | null;
  created_at: string; updated_at: string;
}

async function getJson<T>(url: string, what: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Could not read ${what} (${res.status})`);
  }
  return (await res.json()) as T;
}

export interface ProspectFilters {
  page?: number; q?: string; category?: string; region?: string; status?: string;
  contacted?: string; due?: boolean; retired?: boolean;
}

export const prospectsQuery = (p: ProspectFilters = {}) => queryOptions({
  queryKey: ['admin', 'prospects', p],
  queryFn: () => {
    const s = new URLSearchParams({ page: String(p.page ?? 1), limit: '50' });
    if (p.q?.trim()) s.set('q', p.q.trim());
    for (const k of ['category', 'region', 'status', 'contacted'] as const) if (p[k]) s.set(k, p[k]!);
    if (p.due) s.set('due', '1');
    if (p.retired) s.set('retired', '1');
    return getJson<Paged<Prospect> & {
      stats: { total: number; not_contacted: number; due: number };
      regions: Array<{ id: string; n: number }>;
      choices: ProspectChoice[];
    }>(`/api/prospects?${s.toString()}`, 'the prospects');
  },
  staleTime: 0,
});

export const prospectQuery = (id: string) => queryOptions({
  queryKey: ['admin', 'prospect', id],
  queryFn: () => getJson<{
    prospect: Prospect;
    contacts: Array<{ id: string; name: string; role: string | null; phone: string | null;
      email: string | null; decision_maker: boolean }>;
    activities: Array<{ id: string; kind: string; note: string | null; created_by: string | null;
      created_at: string }>;
    quotes: Array<{ id: string; ref: string; status: string; qty: number;
      total_cents: number | null; est_total_cents: number | null; created_at: string }>;
  }>(`/api/prospects/${id}`, 'the prospect'),
  staleTime: 0,
});

export const prospectChoicesQuery = queryOptions({
  queryKey: ['admin', 'prospect-choices'],
  queryFn: () => getJson<{ choices: ProspectChoice[] }>('/api/prospects/choices', 'the prospect lists'),
  staleTime: 60_000,
});

export const prospectsDueQuery = queryOptions({
  queryKey: ['admin', 'prospects', 'due'],
  queryFn: () => getJson<{ due: number }>('/api/prospects/due', 'the follow-ups'),
  staleTime: 60_000,
});
